import Popup from './Popup'
import * as S from './ActionPopup.styles'
import { analytics, EVENT_NAMES } from '../../services/Analytics/Analytics'
import { useEffect } from 'react'
import { ReactComponent as CloseIcon } from '../../icons/assets/X.svg'

export function ActionPopup({
  popupName,
  imageSource = '',
  imageComponent,
  open,
  mainText,
  secondaryText,
  approveText,
  onApproveTap,
  approveButtonMinWidth = 229,
  dismissText,
  onDismissTap,
  showCloseButton = false,
  onClose = () => {},
  analyticsProps = {},
  titleMaxWidth = undefined,
}: {
  popupName: string
  imageSource?: string
  imageComponent?: React.ReactNode
  open: boolean
  mainText: string
  approveText: string | React.ReactNode
  secondaryText?: string
  onApproveTap: (e?: React.MouseEvent<HTMLDivElement>) => void
  approveButtonMinWidth?: number
  dismissText?: string
  onDismissTap?: (e?: React.MouseEvent<HTMLDivElement>) => void
  showCloseButton?: boolean
  onClose?: () => void
  analyticsProps?: object
  titleMaxWidth?: number
}) {
  useEffect(() => {
    if (!open) return
    analytics.track(EVENT_NAMES.POPUP_DISPLAYED, {
      'Popup Title': popupName,
      ...analyticsProps,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handleApprove = () => {
    analytics.track(EVENT_NAMES.POPUP_CTA_TAPPED, {
      'Popup Title': popupName,
      ...analyticsProps,
    })
    onApproveTap()
  }

  const trackPopupDismissed = () => {
    analytics.track(EVENT_NAMES.POPUP_DISMISSED, {
      'Popup Title': popupName,
      ...analyticsProps,
    })
  }

  const handleDismiss = () => {
    trackPopupDismissed()
    onDismissTap && onDismissTap()
  }

  const handleClose = () => {
    trackPopupDismissed()
    onClose()
  }

  const approveButtonProps = {
    $dualOption: !!dismissText,
    'data-testid': 'action-popup-approve-button',
    minWidth: approveButtonMinWidth,
  }

  return (
    <Popup open={open} maxHeight="fit-content" onClose={onClose}>
      <S.Wrapper data-testid="action-popup" data-popup-name={popupName}>
        {showCloseButton && (
          <S.CloseButton
            onClick={handleClose}
            data-testid="action-popup-close-button"
          >
            <CloseIcon />
          </S.CloseButton>
        )}
        {imageComponent}
        {imageSource && (
          <S.ImageWrapper
            $dualOption={!!dismissText}
            $secondaryText={!!secondaryText}
          >
            <S.Image src={imageSource} />
          </S.ImageWrapper>
        )}
        <S.Title
          $titleMaxWidth={titleMaxWidth}
          $dualOption={!!dismissText}
          $secondaryText={!!secondaryText}
          $addMargin={!imageSource && !imageComponent}
        >
          {mainText}
        </S.Title>
        {secondaryText && <S.Subtitle>{secondaryText}</S.Subtitle>}
        {typeof approveText === 'string' ? (
          <S.ApproveButton onClick={handleApprove} {...approveButtonProps}>
            {approveText}
          </S.ApproveButton>
        ) : (
          <S.ApproveWrapper
            $size="lg"
            $variant="primary"
            {...approveButtonProps}
          >
            {approveText}
          </S.ApproveWrapper>
        )}
        {dismissText && (
          <S.DismissButton
            data-testid="action-popup-dismiss-button"
            onClick={handleDismiss}
          >
            {dismissText}
          </S.DismissButton>
        )}
      </S.Wrapper>
    </Popup>
  )
}
